/**
 * <NextSessionHeroSkeleton> — la forme de `<NextSessionHero>` pendant un chargement.
 *
 * Le visage du tuteur, la date et l'heure en grand, la ligne de la séance dessous. Il grandit aux
 * mêmes marches que le bloc, sur la même mesure (`useNextSessionHeroLayout`) : une retouche de la
 * géométrie se voit ici sans recopie. Comme le bloc, il ne dessine pas sa carte.
 *
 * Ce qu'il ne devine pas : les chips d'état (« Aujourd'hui », « Retard »…), et le bouton d'entrée
 * du parent, qui n'existe que dans sa fenêtre. Celui de l'élève est toujours là (`join`) : sa
 * place est gardée, sous les infos quand le bloc est étroit, à droite quand il est large. Chez le
 * masteur, pas de visage (`lead={false}`) ni la place de son nœud, et sa ligne à lui à la place de
 * celle de la séance (`details`), comme sur sa carte.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { pushButtonBoxHeight } from "../../atoms/Buttons/Button/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Skeleton } from "../../atoms/Skeleton"
import { SessionDetailsLineSkeleton } from "../../molecules/SessionDetailsLine"
import { BOW_ROOM } from "../../molecules/SessionRow/SessionRow"
import { type SpacingKey } from "../../theme/tokens/spacing"
import { useNextSessionHeroLayout } from "./NextSessionHero"

/** « GO ! » à côté des infos : la largeur d'un bouton court. */
const JOIN_WIDTH = 96

export type NextSessionHeroSkeletonProps = {
    /** La place du bouton d'entrée toujours présent (`joinDisplay="always"`). Défaut `false`. */
    join?: boolean
    /** La place du visage du tuteur. Défaut `true` ; `false` chez le masteur, dont la carte n'en a pas. */
    lead?: boolean
    /** La forme de ce que la carte pose à la place de la ligne de la séance (`details`). Absent : celle-ci. */
    details?: React.ReactNode
}

export function NextSessionHeroSkeleton(props: NextSessionHeroSkeletonProps) {
    const { join = false, lead = true, details } = props
    const { onLayout, wide, titleSize, avatarSize, padding } = useNextSessionHeroLayout()

    const info = (
        // À côté du bouton, les infos prennent la largeur qu'il laisse ; seules, elles ont la leur.
        <Row gap="4" align="center" style={[lead ? styles.bow : undefined, join && wide ? styles.fill : undefined]}>
            {lead && <Skeleton shape="circle" size={avatarSize} />}
            <Stack gap="2" style={styles.fill}>
                {/* « Mercredi 30 septembre · 10h00 » : sur une ligne en grand ; sur un
                téléphone, l'heure passe à la ligne (la rangée garde son écart de 4 px). */}
                {wide ? (
                    <Skeleton textSize={titleSize} width={400} />
                ) : (
                    <Stack gap="1">
                        <Skeleton textSize={titleSize} width="85%" />
                        <Skeleton textSize={titleSize} width="30%" />
                    </Stack>
                )}
                {details !== undefined ? details : <SessionDetailsLineSkeleton width={240} />}
            </Stack>
        </Row>
    )

    if (!join)
        return (
            <View onLayout={onLayout} style={styles.hero(padding)}>
                {info}
            </View>
        )

    return (
        <View onLayout={onLayout} style={[styles.hero(padding), wide ? styles.beside : styles.stacked]}>
            {info}
            <Skeleton shape="block" height={pushButtonBoxHeight("md")} width={wide ? JOIN_WIDTH : "100%"} />
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    fill: { flex: 1, flexShrink: 1 },
    bow: { paddingBottom: BOW_ROOM },
    hero: (padding: SpacingKey) => ({
        paddingHorizontal: theme.spacing[padding],
        paddingVertical: theme.spacing[padding],
    }),
    /** Les mêmes écarts que le bloc : le bouton à droite, bas contre bas, ou dessous. */
    beside: {
        flexDirection: "row",
        alignItems: "flex-end",
        gap: theme.spacing["4"],
    },
    stacked: {
        gap: theme.spacing["3"],
    },
}))
